// Monitoring & alerting (PLAN §13.2; task 45): the synthetic checks a Cron runs every 5 minutes, the incidents
// they open (in-app admin banner, email to admins, GitHub Issue), stuck-pipeline handling, budget alerts at
// 50/80/100% of the monthly AI budget, and the daily digest. Pure logic and API shapes, shared by the app Worker
// and the Admin → Monitoring screen.

import type { KeyRotationView } from './operations';
import { INGEST_STAGES, type StageDef } from './progress';

export const MONITOR_CHECKS = ['login', 'api', 'd1', 'r2', 'vectorize', 'ai_gateway', 'queue', 'workflows', 'pipeline'] as const;
export type MonitorCheckKey = (typeof MONITOR_CHECKS)[number];
export const isMonitorCheck = (v: unknown): v is MonitorCheckKey => MONITOR_CHECKS.includes(v as MonitorCheckKey);

/** Plain-language names and what each check proves, for the screen, the banner and the alert texts. */
export const MONITOR_CHECK_LABELS: Record<MonitorCheckKey, { label: string; help: string }> = {
  login: { label: 'Sign-in page', help: 'The sign-in page loads.' },
  api: { label: 'API health', help: 'The app’s API answers its health check.' },
  d1: { label: 'Database (D1)', help: 'The database answers a simple query.' },
  r2: { label: 'File storage (R2)', help: 'The file store answers a lookup.' },
  vectorize: { label: 'Meaning search (Vectorize)', help: 'The meaning-search index answers.' },
  ai_gateway: { label: 'AI Gateway', help: 'The AI Gateway can be reached. No model is called and nothing is spent.' },
  queue: {
    label: 'Processing queue',
    help: 'Files waiting to be processed: the queue backlog, and no file has waited more than 15 minutes to start.',
  },
  workflows: { label: 'Pipeline failures', help: 'Fewer than 3 files failed processing in the last hour.' },
  pipeline: { label: 'Pipeline Worker', help: 'The Worker that runs processing answers its health check.' },
};

/** What people would notice when a check fails, in plain words: the alert emails and notifications lead with this (D472). */
export const MONITOR_CHECK_ALERTS: Record<MonitorCheckKey, { headline: string; notice: string; area: string }> = {
  login: {
    headline: 'People may not be able to sign in: the sign-in page is not loading',
    notice: 'The sign-in page may not open, so people who are signed out may not get back in.',
    area: 'the sign-in page',
  },
  api: {
    headline: 'Hoopoe may not respond: the app is not answering',
    notice: 'Screens may stay empty or show errors.',
    area: 'the app',
  },
  d1: {
    headline: 'Hoopoe may not load or save work: the database is not answering',
    notice: 'Screens may not load, and changes may not be saved.',
    area: 'the database',
  },
  r2: {
    headline: 'Files may not open or upload: file storage is not answering',
    notice: 'Opening, uploading and exporting files may fail.',
    area: 'file storage',
  },
  vectorize: {
    headline: 'Search by meaning may miss results: the meaning-search index is not answering',
    notice: 'Search still finds exact words, but matches by meaning and chat answers may miss things.',
    area: 'search by meaning',
  },
  ai_gateway: {
    headline: 'AI features may be slow or unavailable: the AI service is not answering',
    notice: 'Chat, AI findings and the reading of new uploads may wait or fail.',
    area: 'the AI service',
  },
  queue: {
    headline: 'Uploads may be delayed: files are waiting to be processed',
    notice: 'New uploads may wait longer than usual before processing starts.',
    area: 'the processing queue',
  },
  workflows: {
    headline: 'Some uploads are failing: several files failed processing in the last hour',
    notice: 'Uploads may end as failed. People can retry them once this is fixed.',
    area: 'file processing',
  },
  pipeline: {
    headline: 'Uploads may be delayed: document processing is not answering',
    notice: 'New uploads may wait before processing starts or finishes.',
    area: 'document processing',
  },
};

/** `skipped`: the service isn’t set up in this environment (local development has no AI binding or Vectorize). */
export type MonitorCheckStatus = 'ok' | 'failing' | 'skipped';

/** Consecutive failures (5 minutes apart) before a check opens an incident: one blip doesn't page anyone. */
export const FAILURES_TO_OPEN = 2;
/**
 * A check must have been failing this long before anyone is alerted (notification, email, GitHub Issue). The banner
 * still shows from FAILURES_TO_OPEN runs. Staging, 21–23 Oct 2026: 57 of 60 incidents cleared within 15 minutes (D472).
 */
export const ALERT_AFTER_FAILING_MS = 15 * 60_000;
/** Queue: more waiting messages than this, or a file waiting longer than QUEUE_WAIT_LIMIT_MS, fails the check. */
export const QUEUE_BACKLOG_LIMIT = 200;
export const QUEUE_WAIT_LIMIT_MS = 15 * 60_000;
/** Pipeline failures: this many files failing within WORKFLOW_FAILURE_WINDOW_MS fails the check. */
export const WORKFLOW_FAILURE_LIMIT = 3;
export const WORKFLOW_FAILURE_WINDOW_MS = 60 * 60_000;
/** A probe that takes longer than this counts as failed. */
export const PROBE_TIMEOUT_MS = 10_000;

export interface MonitorCheckView {
  key: MonitorCheckKey;
  label: string;
  help: string;
  status: MonitorCheckStatus | 'pending';
  /** Short, generic reason when failing or skipped (never content). */
  message: string | null;
  consecutiveFailures: number;
  latencyMs: number | null;
  lastRunAt: number | null;
  lastOkAt: number | null;
}

export interface MonitorIncidentView {
  id: string;
  check: MonitorCheckKey;
  label: string;
  message: string | null;
  openedAt: number;
  resolvedAt: number | null;
  /** The GitHub Issue opened for it, when GitHub alerts are set up. */
  issueUrl: string | null;
}

/** What the admin banner shows: open incidents only. */
export interface MonitoringBanner {
  incidents: Pick<MonitorIncidentView, 'id' | 'check' | 'label' | 'openedAt'>[];
}

// ── Pipeline health ─────────────────────────────────────────────────────────────────────────────────────────

/** A processing run with no progress for this many times its stage's stall threshold counts as stuck. */
export const STUCK_FACTOR = 3;
const INGEST_STALL_MAX = Math.max(...INGEST_STAGES.filter(s => Number.isFinite(s.stallAfterMs)).map(s => s.stallAfterMs));
const INGEST_STALL_MIN = Math.min(...INGEST_STAGES.map(s => s.stallAfterMs));
/** Documents untouched for less than this can't be stuck yet (the shortest stage threshold × 3). */
export const STUCK_MIN_MS = STUCK_FACTOR * INGEST_STALL_MIN;

/** How long a run on this stage may go without progress before it is stuck (unknown stage: the longest one). */
export function stuckAfterMs(stage: Pick<StageDef, 'stallAfterMs'> | undefined | null): number {
  const stall = stage && Number.isFinite(stage.stallAfterMs) ? stage.stallAfterMs : INGEST_STALL_MAX;
  return STUCK_FACTOR * stall;
}

/** What the stuck-job sweep does: first time → restart the run once; second time → stop it and flag it to admins. */
export type StuckAction = 'retry' | 'flag';
export const stuckAction = (stallRetries: number): StuckAction => (stallRetries >= 1 ? 'flag' : 'retry');
/** `documents.stall_retries` once a document has been flagged. */
export const STUCK_FLAGGED = 2;

export const STUCK_FAILURE_REASON =
  'Processing stopped making progress twice, so it was stopped. The admins have been told. Try again later.';

export interface StuckJobView {
  documentId: string;
  filename: string;
  uploadedBy: string;
  uploaderName: string | null;
  flaggedAt: number;
}

// ── Budget alerts ───────────────────────────────────────────────────────────────────────────────────────────

export const BUDGET_ALERT_LEVELS = [50, 80, 100] as const;
export type BudgetAlertLevel = (typeof BUDGET_ALERT_LEVELS)[number];

/** Levels this month's spend has reached (cap null or 0: no budget, no alerts). */
export function budgetLevelsReached(spentUsd: number, capUsd: number | null): BudgetAlertLevel[] {
  if (!capUsd || capUsd <= 0) return [];
  return BUDGET_ALERT_LEVELS.filter(l => spentUsd >= (capUsd * l) / 100);
}

/** `YYYY-MM-DD` (UTC). */
export const dayKey = (at: number | Date) => new Date(at).toISOString().slice(0, 10);

export interface BudgetAlertView {
  level: BudgetAlertLevel;
  spentUsd: number;
  capUsd: number;
  createdAt: number;
}

export interface BudgetUseView {
  month: string;
  spentUsd: number;
  capUsd: number | null;
  /** 0–100+, null without a cap. */
  percent: number | null;
  alerts: BudgetAlertView[];
}

export function budgetAlertTitle(level: BudgetAlertLevel) {
  return level >= 100 ? 'AI budget used up for this month' : `AI budget ${level}% used this month`;
}

// ── Daily digest ────────────────────────────────────────────────────────────────────────────────────────────

/** Counts only: the digest goes by email, so it never names a file, a person or anything in the data. */
export interface DigestSummary {
  /** The 24 hours covered, ending at `to` (ms). */
  from: number;
  to: number;
  uploads: number;
  processed: number;
  failed: number;
  stuckRetried: number;
  stuckFlagged: number;
  aiCostUsd: number;
  monthSpentUsd: number;
  monthCapUsd: number | null;
  openIncidents: number;
  failingChecks: number;
  /**
   * Credentials due for rotation (within 30 days of expiry), as reminder lines (task 50). Optional: digests
   * stored before it have none.
   */
  keysToRotate?: string[];
}

export interface DigestView {
  day: string;
  sentAt: number;
  recipients: number;
  summary: DigestSummary;
}

const usd = (n: number) => `$${n.toFixed(2)}`;

export function digestLines(s: DigestSummary): string[] {
  const pct = s.monthCapUsd ? Math.round((s.monthSpentUsd / s.monthCapUsd) * 100) : null;
  return [
    `Uploads: ${s.uploads}`,
    `Processed: ${s.processed}`,
    `Failed: ${s.failed}`,
    `Stuck jobs restarted: ${s.stuckRetried} · flagged: ${s.stuckFlagged}`,
    `AI cost (24 h): ${usd(s.aiCostUsd)}`,
    s.monthCapUsd
      ? `AI budget this month: ${usd(s.monthSpentUsd)} of ${usd(s.monthCapUsd)} (${pct}%)`
      : `AI spend this month: ${usd(s.monthSpentUsd)} (no budget set)`,
    s.openIncidents || s.failingChecks
      ? `System checks: ${s.failingChecks} failing, ${s.openIncidents} open incident${s.openIncidents === 1 ? '' : 's'}`
      : 'System checks: all passing',
    ...(s.keysToRotate ?? []).map(k => `Key to rotate: ${k} (see the Rotate keys runbook)`),
  ];
}

/** The digest email (plain text, counts only). */
export function digestText(s: DigestSummary, env: string): string {
  return [
    `Hoopoe daily digest (${env}), last 24 hours to ${new Date(s.to).toISOString().slice(0, 16).replace('T', ' ')} UTC`,
    '',
    ...digestLines(s),
    '',
    'Details: Admin → Monitoring.',
    '',
    'Hoopoe',
  ].join('\n');
}

export interface MonitoringOverview {
  environment: string;
  checks: MonitorCheckView[];
  incidents: MonitorIncidentView[];
  stuck: StuckJobView[];
  budget: BudgetUseView;
  lastDigest: DigestView | null;
  /** Credentials with an expiry date, and whether each is due for rotation (task 50). */
  keyRotations: KeyRotationView[];
  /** Which alert channels are set up (the GitHub one needs a token secret). */
  channels: { banner: true; email: true; github: boolean };
}

// ── Incident alerts ─────────────────────────────────────────────────────────────────────────────────────────

const when = (ms: number) => {
  const d = new Date(ms);
  return `${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })} ${d.toISOString().slice(11, 16)} UTC`;
};
const minutes = (ms: number) => {
  const m = Math.max(1, Math.round(ms / 60_000));
  return m < 120 ? `${m} minute${m === 1 ? '' : 's'}` : `${Math.round(m / 60)} hours`;
};

/** In-app titles for an incident (generic: never content). */
export const incidentAlertTitle = (key: MonitorCheckKey, resolved = false) =>
  resolved ? `Working again: ${MONITOR_CHECK_ALERTS[key].area}` : MONITOR_CHECK_ALERTS[key].headline;

/**
 * The incident emails: the failing one and, in the same thread, the "working again" reply. The subject carries the
 * start time, so each incident is its own thread; what people would notice comes first, the technical name last.
 */
export function incidentEmail(
  key: MonitorCheckKey,
  o: { since: number; now: number; message: string | null; env: string; resolved?: boolean },
): { subject: string; text: string } {
  const a = MONITOR_CHECK_ALERTS[key];
  const subject = `Hoopoe${o.env === 'production' ? '' : ` (${o.env})`}: ${a.headline} (since ${when(o.since)})`;
  const technical = ['Technical detail', `Check: ${MONITOR_CHECK_LABELS[key].label} (${key})`, `Environment: ${o.env}`];
  if (o.resolved)
    return {
      subject: `Re: ${subject}`,
      text: [
        `Working again: ${a.area} answers normally since ${when(o.now)}.`,
        '',
        `It was failing for about ${minutes(o.now - o.since)}, from ${when(o.since)}. Nothing else needs to be done.`,
        '',
        ...technical,
      ].join('\n'),
    };
  return {
    subject,
    text: [
      `${a.headline}.`,
      '',
      `What people may notice: ${a.notice}`,
      `Since: ${when(o.since)} (about ${minutes(o.now - o.since)} so far).`,
      '',
      'Hoopoe keeps checking every 5 minutes and will reply in this thread when it works again. Details are in Admin → Monitoring.',
      '',
      ...technical,
      `Last error: ${o.message ?? 'not given'}`,
    ].join('\n'),
  };
}
